import Dexie from "dexie";
import {
  countSubmissionStorageKey,
  draftCompositeId,
  legacyCountSubmissionStorageKey,
  normalizePeriodKey,
  META_INVENTORY_KEY,
  isOfflineDraftsEnabled
} from "./countDraftConstants";

let dbPromise = null;
let initError = null;

class CountDraftDatabase extends Dexie {
  constructor() {
    super("SimplyInventoryCountDrafts");
    this.version(1).stores({
      countDrafts:
        "id, tenantId, userId, siteId, currentPeriodKey, inventoryItemId, syncStatus, [tenantId+userId+siteId+currentPeriodKey]",
      syncQueue:
        "mutationId, tenantId, userId, siteId, currentPeriodKey, [tenantId+userId+siteId+currentPeriodKey], nextRetryAt"
    });
    // Pending final "Submit to Manager" payloads (distinct from draft line sync).
    this.version(2).stores({
      countDrafts:
        "id, tenantId, userId, siteId, currentPeriodKey, inventoryItemId, syncStatus, [tenantId+userId+siteId+currentPeriodKey]",
      syncQueue:
        "mutationId, tenantId, userId, siteId, currentPeriodKey, [tenantId+userId+siteId+currentPeriodKey], nextRetryAt",
      finalSubmissions:
        "id, clientSubmissionId, tenantId, userId, siteId, currentPeriodKey, status, [tenantId+userId+siteId+currentPeriodKey], [tenantId+clientSubmissionId]"
    });
  }
}

/** Composite primary key for one pending final submission per tenant/user/site/period. */
export function finalSubmissionCompositeId(tenantId, userId, siteId, periodKey) {
  return `${tenantId}|${userId}|${siteId}|${periodKey}|__final_submit__`;
}

export function getCountDraftInitError() {
  return initError;
}

export async function getDb() {
  if (!isOfflineDraftsEnabled()) return null;
  if (initError) return null;
  if (dbPromise) return dbPromise;
  dbPromise = (async () => {
    try {
      const d = new CountDraftDatabase();
      await d.open();
      return d;
    } catch (e) {
      initError = e;
      console.error("[countDraftDb] Dexie init failed:", e);
      return null;
    }
  })();
  return dbPromise;
}

export async function upsertLocalDraft(row) {
  const db = await getDb();
  if (!db) return;
  await db.countDrafts.put(row);
}

export async function getLocalDraft(tenantId, userId, siteId, periodKey, inventoryItemId) {
  const db = await getDb();
  if (!db) return null;
  const id = draftCompositeId(tenantId, userId, siteId, periodKey, inventoryItemId);
  return db.countDrafts.get(id);
}

export async function getLocalDraftsForScreen(tenantId, userId, siteId, periodKey) {
  const db = await getDb();
  if (!db) return [];
  return db.countDrafts
    .where("[tenantId+userId+siteId+currentPeriodKey]")
    .equals([tenantId, userId, siteId, periodKey])
    .toArray();
}

export async function enqueueDraftMutation(record) {
  const db = await getDb();
  if (!db) return;
  await db.syncQueue.put(record);
}

export async function markDraftSynced(tenantId, userId, siteId, periodKey, inventoryItemId, serverUpdatedAt) {
  const db = await getDb();
  if (!db) return;
  const id = draftCompositeId(tenantId, userId, siteId, periodKey, inventoryItemId);
  const row = await db.countDrafts.get(id);
  if (row) {
    row.syncStatus = "synced";
    row.lastSyncedAt = new Date().toISOString();
    row.lastError = null;
    if (serverUpdatedAt) row.serverUpdatedAt = serverUpdatedAt;
    await db.countDrafts.put(row);
  }
}

export async function markDraftError(tenantId, userId, siteId, periodKey, inventoryItemId, errMsg) {
  const db = await getDb();
  if (!db) return;
  const id = draftCompositeId(tenantId, userId, siteId, periodKey, inventoryItemId);
  const row = await db.countDrafts.get(id);
  if (row) {
    row.syncStatus = "error";
    row.lastError = errMsg || "Sync error";
    await db.countDrafts.put(row);
  }
}

export async function removeQueueMutation(mutationId) {
  const db = await getDb();
  if (!db) return;
  await db.syncQueue.delete(mutationId);
}

export async function getPendingQueueItems(tenantId, userId, siteId, periodKey) {
  const db = await getDb();
  if (!db) return [];
  const now = Date.now();
  const all = await db.syncQueue
    .where("[tenantId+userId+siteId+currentPeriodKey]")
    .equals([tenantId, userId, siteId, periodKey])
    .toArray();
  return all.filter((q) => !q.nextRetryAt || new Date(q.nextRetryAt).getTime() <= now);
}

export async function getUnsyncedStateForScreen(tenantId, userId, siteId, periodKey) {
  const drafts = await getLocalDraftsForScreen(tenantId, userId, siteId, periodKey);
  const pendingDraft = drafts.some((d) => ["pending", "syncing", "error"].includes(d.syncStatus));
  const queue = await getPendingQueueItems(tenantId, userId, siteId, periodKey);
  return { pendingDraft, anyQueued: queue.length > 0, drafts, queue };
}

/** After a successful bulk server draft save or manual retry: clear terminal error so lines can re-queue and sync. */
export async function resetErrorDraftsToPending(tenantId, userId, siteId, periodKey) {
  const db = await getDb();
  if (!db) return 0;
  const rows = await getLocalDraftsForScreen(tenantId, userId, siteId, periodKey);
  let n = 0;
  for (const row of rows) {
    if (row.syncStatus === "error") {
      row.syncStatus = "pending";
      row.lastError = null;
      await db.countDrafts.put(row);
      n += 1;
    }
  }
  return n;
}

export async function clearTenantUserDraftView(tenantId, userId) {
  const db = await getDb();
  if (!db) return;
  const toDelDrafts = await db.countDrafts.where("tenantId").equals(tenantId).and((r) => r.userId === userId).toArray();
  const toDelQ = await db.syncQueue.where("tenantId").equals(tenantId).and((r) => r.userId === userId).toArray();
  const toDelFinal = db.finalSubmissions
    ? await db.finalSubmissions.where("tenantId").equals(tenantId).and((r) => r.userId === userId).toArray()
    : [];
  const tables = [db.countDrafts, db.syncQueue];
  if (db.finalSubmissions) tables.push(db.finalSubmissions);
  await db.transaction("rw", tables, async () => {
    for (const r of toDelDrafts) await db.countDrafts.delete(r.id);
    for (const r of toDelQ) await db.syncQueue.delete(r.mutationId);
    for (const r of toDelFinal) await db.finalSubmissions.delete(r.id);
  });
}

export async function clearScreenDrafts(tenantId, userId, siteId, periodKey) {
  const db = await getDb();
  if (!db) return;
  const rows = await getLocalDraftsForScreen(tenantId, userId, siteId, periodKey);
  const qrows = await db.syncQueue
    .where("[tenantId+userId+siteId+currentPeriodKey]")
    .equals([tenantId, userId, siteId, periodKey])
    .toArray();
  await db.transaction("rw", db.countDrafts, db.syncQueue, async () => {
    for (const r of rows) await db.countDrafts.delete(r.id);
    for (const r of qrows) await db.syncQueue.delete(r.mutationId);
  });
}

export async function upsertFinalSubmission(row) {
  const db = await getDb();
  if (!db || !db.finalSubmissions) return null;
  await db.finalSubmissions.put(row);
  return row;
}

export async function getFinalSubmissionForScreen(tenantId, userId, siteId, periodKey) {
  const db = await getDb();
  if (!db || !db.finalSubmissions) return null;
  const id = finalSubmissionCompositeId(tenantId, userId, siteId, periodKey);
  return db.finalSubmissions.get(id);
}

export async function getPendingFinalSubmissions(tenantId, userId) {
  const db = await getDb();
  if (!db || !db.finalSubmissions) return [];
  const all = await db.finalSubmissions
    .where("tenantId")
    .equals(tenantId)
    .and((r) => r.userId === userId)
    .toArray();
  // Auto-retry only pending/syncing (interrupted). Terminal failed/stale stay until user acts.
  return all.filter((r) => r.status === "pending" || r.status === "syncing");
}

export async function updateFinalSubmission(id, patch, expectedClientSubmissionId = null) {
  const db = await getDb();
  if (!db || !db.finalSubmissions) return null;
  const row = await db.finalSubmissions.get(id);
  if (!row) return null;
  if (
    expectedClientSubmissionId &&
    row.clientSubmissionId !== expectedClientSubmissionId
  ) {
    return null;
  }
  const next = { ...row, ...patch };
  await db.finalSubmissions.put(next);
  return next;
}

export async function deleteFinalSubmission(id, expectedClientSubmissionId = null) {
  const db = await getDb();
  if (!db || !db.finalSubmissions) return;
  if (expectedClientSubmissionId) {
    const row = await db.finalSubmissions.get(id);
    if (!row || row.clientSubmissionId !== expectedClientSubmissionId) return;
  }
  await db.finalSubmissions.delete(id);
}

export async function clearFinalSubmissionForScreen(tenantId, userId, siteId, periodKey) {
  const db = await getDb();
  if (!db || !db.finalSubmissions) return;
  const id = finalSubmissionCompositeId(tenantId, userId, siteId, periodKey);
  await db.finalSubmissions.delete(id);
}

export async function migrateLegacyLocalDrafts({
  tenantId,
  userId,
  siteId,
  periodKey,
  countingPeriodStartedAt,
  inventoryIds
}) {
  if (!isOfflineDraftsEnabled()) return;
  const db = await getDb();
  if (!db) return;
  const flagKey = "si_idb_migrated_" + tenantId + "_" + userId + "_" + siteId + "_" + periodKey;
  try {
    if (localStorage.getItem(flagKey) === "1") return;
  } catch (_) {
    return;
  }
  let raw;
  try {
    raw =
      localStorage.getItem(countSubmissionStorageKey(userId, siteId, countingPeriodStartedAt)) ||
      localStorage.getItem(legacyCountSubmissionStorageKey(userId, siteId));
  } catch (_) {
    return;
  }
  if (!raw) {
    try {
      localStorage.setItem(flagKey, "1");
    } catch (_) {}
    return;
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (_) {
    return;
  }
  if (parsed.selectedSiteId && parsed.selectedSiteId !== siteId) return;
  if (
    parsed.periodStartedAt !== undefined &&
    normalizePeriodKey(countingPeriodStartedAt) !== normalizePeriodKey(parsed.periodStartedAt)
  ) {
    return;
  }
  const now = new Date().toISOString();
  await db.transaction("rw", db.countDrafts, async () => {
    if (parsed.personName || parsed.generalNotes) {
      await db.countDrafts.put({
        id: draftCompositeId(tenantId, userId, siteId, periodKey, META_INVENTORY_KEY),
        tenantId,
        userId,
        siteId,
        currentPeriodKey: periodKey,
        inventoryItemId: META_INVENTORY_KEY,
        quantity: "",
        quantity2: "",
        notes: "",
        personName: parsed.personName || "",
        generalNotes: parsed.generalNotes || "",
        updatedAt: now,
        syncStatus: "pending",
        lastSyncedAt: null,
        lastError: null,
        serverUpdatedAt: null
      });
    }
    if (parsed.counts && typeof parsed.counts === "object") {
      for (const iid of inventoryIds) {
        const c = parsed.counts[iid];
        if (!c) continue;
        const has =
          (c.quantity !== "" && c.quantity !== undefined) ||
          (c.quantity2 !== "" && c.quantity2 !== undefined) ||
          (c.notes && String(c.notes).trim() !== "");
        if (!has) continue;
        await db.countDrafts.put({
          id: draftCompositeId(tenantId, userId, siteId, periodKey, iid),
          tenantId,
          userId,
          siteId,
          currentPeriodKey: periodKey,
          inventoryItemId: iid,
          quantity: c.quantity ?? "",
          quantity2: c.quantity2 ?? "",
          notes: c.notes ?? "",
          updatedAt: now,
          syncStatus: "pending",
          lastSyncedAt: null,
          lastError: null,
          serverUpdatedAt: null
        });
      }
    }
  });
  try {
    localStorage.setItem(flagKey, "1");
  } catch (_) {}
}

export async function removeQueuedMutationsForTarget(tenantId, userId, siteId, periodKey, inventoryItemId) {
  const db = await getDb();
  if (!db) return;
  const all = await db.syncQueue
    .where("[tenantId+userId+siteId+currentPeriodKey]")
    .equals([tenantId, userId, siteId, periodKey])
    .toArray();
  for (const q of all) {
    const p = q.payload || {};
    const inv = p.inventoryItemId;
    const match =
      inventoryItemId == null
        ? inv == null || inv === undefined
        : inv === inventoryItemId;
    if (match) await db.syncQueue.delete(q.mutationId);
  }
}
